import type { FC, FormEvent, ReactNode } from 'react'

import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import {
    ArrowRight,
    Check,
    ChevronDown,
    LoaderCircle,
    X
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { LogoMark } from '@/components/generals/logo'
import { useTrial } from '@/components/generals/trial-provider'

const TEAM_SIZES = [
    'Just me',
    '2 to 10 people',
    '11 to 50 people',
    '51 to 200 people',
    '201 to 1,000 people',
    'More than 1,000 people'
]

const FALLBACK_ERROR = 'Something went wrong on our side. Please try again in a moment.'

const fieldClass = 'h-12 w-full rounded-2xl border border-input bg-white px-4 text-[15px] text-ink outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-muted-foreground/60 focus:border-moss focus:ring-4 focus:ring-meadow/35'

type Status = 'idle' | 'submitting' | 'success' | 'error'

type Submitted = {
    name: string
    email: string
}

interface TrialFormProps {
    source: string
    onClose: () => void
}

const TrialForm: FC<TrialFormProps> = ({ source, onClose }): ReactNode => {
    const [status, setStatus] = useState<Status>('idle')
    const [error, setError] = useState('')
    const [submitted, setSubmitted] = useState<Submitted | null>(null)

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const data = new FormData(event.currentTarget)
        const payload = {
            name: String(data.get('name') ?? '').trim(),
            email: String(data.get('email') ?? '').trim(),
            company: String(data.get('company') ?? '').trim(),
            teamSize: String(data.get('teamSize') ?? ''),
            website: String(data.get('website') ?? ''),
            source
        }

        setStatus('submitting')
        setError('')

        try {
            const response = await fetch('/api/trial', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            })
            const result: { error?: string } = await response.json().catch(() => ({}))

            if (!response.ok) {
                throw new Error(result.error || FALLBACK_ERROR)
            }

            setSubmitted({ name: payload.name, email: payload.email })
            setStatus('success')
        } catch (caught) {
            setError(caught instanceof Error && caught.message ? caught.message : FALLBACK_ERROR)
            setStatus('error')
        }
    }

    if (status === 'success' && submitted) {
        const firstName = submitted.name.split(/\s+/)[0]

        return (
            <div className='flex flex-col items-center px-7 pt-14 pb-10 text-center sm:px-10'>
                <span className='flex size-14 items-center justify-center rounded-full bg-meadow text-ink'>
                    <Check className='size-6' strokeWidth={2.5} />
                </span>

                <h2 id='trial-title' className='mt-6 font-heading text-[30px] leading-[1.1] tracking-[-0.03em] text-balance'>
                    You're in, {firstName}.
                </h2>

                <p className='mt-3 max-w-[36ch] text-[15px] leading-relaxed text-muted-foreground'>
                    We saved your spot and will send your workspace invite to{' '}
                    <span className='font-medium break-all text-ink'>{submitted.email}</span>.
                </p>

                <button
                    type='button'
                    onClick={onClose}
                    className='mt-8 inline-flex h-12 cursor-pointer items-center justify-center rounded-full bg-ink px-6 text-[15px] font-semibold text-white transition-colors hover:bg-ink/85 focus-visible:ring-2 focus-visible:ring-moss focus-visible:ring-offset-2 focus-visible:outline-none'
                >
                    Back to the site
                </button>
            </div>
        )
    }

    const isSubmitting = status === 'submitting'

    return (
        <form onSubmit={handleSubmit} className='px-7 pt-9 pb-8 sm:px-10 sm:pt-10'>
            <span className='flex size-11 items-center justify-center rounded-full bg-meadow text-ink'>
                <LogoMark className='h-[15px]' />
            </span>

            <h2 id='trial-title' className='mt-6 font-heading text-[30px] leading-[1.1] tracking-[-0.03em]'>
                Start your free trial
            </h2>

            <p className='mt-2 text-[15px] leading-relaxed text-muted-foreground'>
                Your first agent can be running this afternoon. Free to start, no credit card needed.
            </p>

            <div className='mt-7 grid gap-4'>
                <label className='grid gap-1.5'>
                    <span className='text-[13px] font-medium text-ink/80'>
                        Full name
                    </span>

                    <input
                        name='name'
                        type='text'
                        required
                        maxLength={120}
                        autoComplete='name'
                        placeholder='Jordan Rivera'
                        className={fieldClass}
                    />
                </label>

                <label className='grid gap-1.5'>
                    <span className='text-[13px] font-medium text-ink/80'>
                        Work email
                    </span>

                    <input
                        name='email'
                        type='email'
                        required
                        maxLength={200}
                        autoComplete='email'
                        placeholder='jordan@company.com'
                        className={fieldClass}
                    />
                </label>

                <div className='grid gap-4 sm:grid-cols-2'>
                    <label className='grid gap-1.5'>
                        <span className='text-[13px] font-medium text-ink/80'>
                            Company <span className='font-normal text-muted-foreground'>(optional)</span>
                        </span>

                        <input
                            name='company'
                            type='text'
                            maxLength={120}
                            autoComplete='organization'
                            placeholder='Northwind'
                            className={fieldClass}
                        />
                    </label>

                    <label className='grid gap-1.5'>
                        <span className='text-[13px] font-medium text-ink/80'>
                            Team size
                        </span>

                        <span className='relative block'>
                            <select
                                name='teamSize'
                                defaultValue=''
                                className={cn(fieldClass, 'cursor-pointer appearance-none pr-10')}
                            >
                                <option value='' disabled>
                                    Choose one
                                </option>

                                {TEAM_SIZES.map((size) => (
                                    <option key={size} value={size}>
                                        {size}
                                    </option>
                                ))}
                            </select>

                            <ChevronDown aria-hidden className='pointer-events-none absolute top-1/2 right-4 size-4 -translate-y-1/2 text-muted-foreground' />
                        </span>
                    </label>
                </div>
            </div>

            <div aria-hidden='true' className='absolute top-auto -left-[9999px] h-px w-px overflow-hidden'>
                <label>
                    Leave this field empty
                    <input type='text' name='website' tabIndex={-1} autoComplete='off' />
                </label>
            </div>

            {status === 'error' && error && (
                <p role='alert' className='mt-5 rounded-2xl bg-red-50 px-4 py-3 text-[14px] leading-snug text-red-700'>
                    {error}
                </p>
            )}

            <button
                type='submit'
                disabled={isSubmitting}
                className='mt-6 inline-flex h-12 w-full cursor-pointer items-center justify-center gap-2 rounded-full bg-moss px-6 text-[16px] font-semibold text-white transition-colors hover:bg-moss-deep focus-visible:ring-2 focus-visible:ring-moss focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-wait disabled:opacity-80'
            >
                {isSubmitting ? (
                    <>
                        <LoaderCircle aria-hidden className='size-4 animate-spin' />
                        Setting up your trial
                    </>
                ) : (
                    <>
                        Start free trial
                        <ArrowRight aria-hidden className='size-4' />
                    </>
                )}
            </button>

            <p className='mt-4 text-center text-[12.5px] leading-relaxed text-muted-foreground'>
                By continuing you agree to our{' '}
                <Link href='/legal' onClick={onClose} className='underline underline-offset-2 hover:text-ink'>
                    terms and privacy policy
                </Link>
                .
            </p>
        </form>
    )
}

export const TrialDialog: FC = (): ReactNode => {
    const { isOpen, source, openCount, closeTrial } = useTrial()
    const dialogRef = useRef<HTMLDialogElement>(null)
    const pressStartedOnBackdrop = useRef(false)

    useEffect(() => {
        const dialog = dialogRef.current

        if (!dialog) return

        if (isOpen) {
            if (!dialog.open) dialog.showModal()

            document.documentElement.style.overflow = 'hidden'
            dialog.querySelector<HTMLInputElement>('input[name="name"]')?.focus()

            return
        }

        if (dialog.open) dialog.close()

        document.documentElement.style.overflow = ''
    }, [isOpen, openCount])

    useEffect(() => {
        return () => {
            document.documentElement.style.overflow = ''
        }
    }, [])

    return (
        <dialog
            ref={dialogRef}
            aria-labelledby='trial-title'
            onClose={closeTrial}
            onPointerDown={(event) => {
                pressStartedOnBackdrop.current = event.target === event.currentTarget
            }}
            onClick={(event) => {
                if (event.target === event.currentTarget && pressStartedOnBackdrop.current) closeTrial()

                pressStartedOnBackdrop.current = false
            }}
            className='m-auto max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-[480px] overflow-y-auto overscroll-contain rounded-[32px] border border-border bg-white p-0 text-ink shadow-[0_40px_120px_-30px_rgba(20,23,15,0.5)] backdrop:bg-ink/40 backdrop:backdrop-blur-[3px] open:animate-in open:fade-in-0 open:zoom-in-95 open:duration-200'
        >
            <div className='relative'>
                <button
                    type='button'
                    onClick={closeTrial}
                    aria-label='Close'
                    className='absolute top-4 right-4 z-10 flex size-10 cursor-pointer items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-ink focus-visible:ring-2 focus-visible:ring-moss focus-visible:outline-none'
                >
                    <X className='size-5' />
                </button>

                {isOpen && (
                    <TrialForm key={openCount} source={source} onClose={closeTrial} />
                )}
            </div>
        </dialog>
    )
}
